import React, { useEffect, useState, createElement } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Hero } from './components/Hero';
import { MarketingHero } from './components/MarketingHero';
import { ProductDemo } from './components/ProductDemo';
import { AICoworkers } from './components/AICoworkers';
import { StayInControl } from './components/StayInControl';
import { ToolComparison } from './components/ToolComparison';
import { HowItWorks } from './components/HowItWorks';
import { HowItWorksConnections } from './components/HowItWorksConnections';
import { AlwaysOn } from './components/AlwaysOn';
import { CapabilityGrid } from './components/CapabilityGrid';
import { QuickCapture } from './components/QuickCapture';
import { LivesInSlack } from './components/LivesInSlack';
import { ConnectedWorkflow } from './components/ConnectedWorkflow';
import { ComputerUse } from './components/ComputerUse';
import { Pricing } from './pages/Pricing';
import { WallOfLove } from './components/WallOfLove';
import { NarrativeSection } from './components/NarrativeSection';
import { MemorySection } from './components/MemorySection';
import { SecuritySection } from './components/SecuritySection';
import { PricingSection } from './components/PricingSection';
import { ClosingCTA } from './components/ClosingCTA';
import { Layout } from './components/Layout';
import { IntroSplash } from './components/IntroSplash';
import { Terms } from './pages/Terms';
import { AffiliateTerms } from './pages/AffiliateTerms';
import { Privacy } from './pages/Privacy';
import { VulnerabilityDisclosure } from './pages/VulnerabilityDisclosure';
import { Contact } from './pages/Contact';
import { Support } from './pages/Support';
import { Careers } from './pages/Careers';
import { Enterprise } from './pages/Enterprise';
import { EnterprisePlatform } from './pages/EnterprisePlatform';
import { ClientManagement } from './pages/ClientManagement';
import { GrowthMarketing } from './pages/GrowthMarketing';
import { FoundersExecs } from './pages/FoundersExecs';
import { Signup } from './pages/Signup';
import { TrialFlow } from './pages/TrialFlow';
import { Blog } from './pages/Blog';
import { Research } from './pages/Research';
import { CaseStudies } from './pages/CaseStudies';
import { Changelog } from './pages/Changelog';
import { Security } from './pages/Security';
import { Integrations } from './pages/Integrations';
import { IntegrationDetail } from './pages/IntegrationDetail';
import { Bespoke } from './pages/Bespoke';
import { Consulting } from './pages/Consulting';
import { CompareChatGPT } from './pages/CompareChatGPT';
import { GigaForChatGPT } from './pages/GigaForChatGPT';
import { GigaForChatGPT2 } from './pages/GigaForChatGPT2';
import { CompanyBrain } from './pages/CompanyBrain';
import { WatchGigaDemo } from './pages/WatchGigaDemo';
import { BookDemo } from './pages/BookDemo';
import { Agents } from './pages/Agents';
import { Home } from './pages/Home';
import { HomeAlternative } from './pages/HomeAlternative';
import { HomeArchitecture } from './pages/HomeArchitecture';
import { EcommerceHome } from './pages/EcommerceHome';
import { FutureHome } from './pages/FutureHome';
import { WorkspaceHome } from './pages/WorkspaceHome';
import { HomeTeamWorkspace } from './pages/HomeTeamWorkspace';
import { ReferralCapture } from './pages/ReferralCapture';
import { Dashboard } from './pages/Dashboard';
import { WorkflowShow } from './pages/WorkflowShow';
import { OnboardingFunnel } from './pages/OnboardingFunnel';
import { WorkspaceAlreadyConnected } from './pages/WorkspaceAlreadyConnected';
import { SphereDirections } from './pages/SphereDirections';
import { ChatTransition } from './pages/ChatTransition';
import { UseCasesConcepts } from './pages/UseCasesConcepts';
import { FontOptions } from './pages/FontOptions';
import { ClaudeFont } from './pages/ClaudeFont';
import { CreatorProgram } from './pages/CreatorProgram';
import { ViktorAlternative } from './pages/ViktorAlternative';
import { FeatureDetail } from './pages/FeatureDetail';
import { SignIn } from './pages/SignIn';
import { Developers } from './pages/Developers';
import { NewInfrastructure } from './pages/NewInfrastructure';
import { Alt } from './pages/Alt';
import { ExportDashboards } from './pages/ExportDashboards';
import { CopyModeProvider } from './components/copy/copyMode';
import { resolveRouteMeta, isNoindexRoute } from './lib/routeMeta';
/**
 * Scrolls the window to the top on every route change.
 */
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
/**
 * Sets <title> and <meta name="description"> per route.
 * Gets baked into each static HTML file by the prerenderer.
 */
function PageMeta() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    // Titles/descriptions live in src/lib/routeMeta.ts so the build-time
    // prerender can bake the SAME values into each static HTML file.
    const {
      title,
      desc
    } = resolveRouteMeta(pathname);
    document.title = title;
    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalHref = `https://getgiga.com${pathname === '/' ? '/' : pathname}`;
    if (!isNoindexRoute(pathname)) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalHref);
    } else if (canonical) {
      canonical.remove();
    }
    const meta = document.querySelector('meta[name="description"]');
    if (desc) {
      if (meta) {
        meta.setAttribute('content', desc);
      } else {
        const el = document.createElement('meta');
        el.name = 'description';
        el.content = desc;
        document.head.appendChild(el);
      }
    }
    // Keep search engines out of the /unlisted and /app-screen-mockups
    // namespaces. This mirrors the noindex meta baked into the prerendered
    // HTML (see prerender.mjs) so it's correct on client-side navigation too.
    let robots = document.querySelector('meta[name="robots"]');
    if (isNoindexRoute(pathname)) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    } else if (robots) {
      // Returning to a public route — remove the restrictive directive.
      robots.remove();
    }
  }, [pathname]);
  return null;
}
export function App() {
  return <CopyModeProvider data-id="element-0" data-mp="2pgmxux74xz45">
      <ScrollToTop data-id="element-1" data-mp="2pig4ichr0jc5" />
      <PageMeta data-id="element-2" data-mp="2phy9fj6tob5t" />
      {/* Global Prado-style film grain over the entire app */}
      <div className="grain-global" aria-hidden="true" data-id="element-3" data-mp="33hpl3d9v9fgn" />
      <Routes data-id="element-4" data-mp="2yi37v3u1fz7u">
        {/* ──────────────────────────────────────────────────────────────
         APP SCREEN MOCKUPS — product/app UI mockups the marketing team uses
         for reviews. These are NOT the live product (the real signup/login
         lives on app.getgiga.com). Grouped under /app-screen-mockups and
         marked noindex (see routeMeta.isNoindexRoute) so search engines skip
         them. Not linked from the site nav.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/app-screen-mockups/signup" element={<Signup data-id="element-6" data-mp="2zb26ngsg7877" />} data-id="element-5" data-mp="2zcvegzesrygs" />
        {/* Direct routes onto individual signup screens, so they can be opened
         from the route dropdown without clicking through the flow. */}
        <Route path="/app-screen-mockups/google-consent" element={<Signup data-id="element-8" data-mp="2udsj66omd6m0" />} data-id="element-7" data-mp="2pgacyo1zl5hp" />
        <Route path="/app-screen-mockups/google-permissions" element={<Signup data-id="element-10" data-mp="2ge9e5mr3ubdx" />} data-id="element-9" data-mp="2ud8zoljbma5v" />
        <Route path="/app-screen-mockups/giga-chat" element={<Signup data-id="element-12" data-mp="2gf2lopxw14x4" />} data-id="element-11" data-mp="2gdra43y7asvg" />
        <Route path="/app-screen-mockups/sign-in" element={<SignIn data-id="element-14" data-mp="2kekwutzwq1mr" />} data-id="element-13" data-mp="2kfuyft5yxry5" />
        <Route path="/app-screen-mockups/trial" element={<TrialFlow data-id="element-16" data-mp="30vx3juoqhvkq" />} data-id="element-15" data-mp="2kf304akzlg46" />
        <Route path="/app-screen-mockups/dashboard" element={<Dashboard data-id="element-18" data-mp="2vwap5hx669ac" />} data-id="element-17" data-mp="30uc4psvdzqh2" />
        <Route path="/app-screen-mockups/workflow-show" element={<WorkflowShow data-id="element-20" data-mp="2vu7om3ye4xrd" />} data-id="element-19" data-mp="2vvr3pr8b92rk" />
        <Route path="/app-screen-mockups/onboarding-funnel" element={<OnboardingFunnel data-id="element-22" data-mp="2qvcysi20a8xr" />} data-id="element-21" data-mp="2qwxtc7oblrvm" />
        <Route path="/app-screen-mockups/slack-oauth-callback" element={<WorkspaceAlreadyConnected data-id="element-24" data-mp="2lxuyfg22121d" />} data-id="element-23" data-mp="2ly4m2wzmmx3w" />
        {/* Figma export surface for the home page's Workspace screens. */}
        <Route path="/app-screen-mockups/export-dashboards" element={<ExportDashboards data-id="element-26" data-mp="2d512s82avq1c" />} data-id="element-25" data-mp="2hycd4j6bmoy0" />

        {/* Waitlist referral capture stays PUBLIC + functional: /r/<code>
         records the inbound referral and forwards to app.getgiga.com. */}
        <Route path="/r/:code" element={<ReferralCapture data-id="element-28" data-mp="2396hf56ozguv" />} data-id="element-27" data-mp="238eknr1czd30" />

        {/* ──────────────────────────────────────────────────────────────
         UNLISTED (standalone, no marketing chrome) — internal review /
         experimental screens. Grouped under /unlisted and marked noindex.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/unlisted/sphere-directions" element={<SphereDirections data-id="element-30" data-mp="dpg8j782qw4g" />} data-id="element-29" data-mp="9r8y0u47er1p" />
        <Route path="/unlisted/chat-transition" element={<ChatTransition data-id="element-32" data-mp="ipb2jjn5c1tq" />} data-id="element-31" data-mp="dskot2ytdibh" />
        <Route path="/unlisted/use-cases-concepts" element={<UseCasesConcepts data-id="element-34" data-mp="nof9e3mllngw" />} data-id="element-33" data-mp="iq41vzjrzp1u" />
        <Route path="/unlisted/font-options" element={<FontOptions data-id="element-36" data-mp="nmbak03k3enh" />} data-id="element-35" data-mp="nnmd5wl9mgd0" />
        <Route path="/unlisted/claude-font" element={<ClaudeFont data-id="element-38" data-mp="3m7w0hwsf3790" />} data-id="element-37" data-mp="3m6tadr2x58u5" />

        {/* ──────────────────────────────────────────────────────────────
         UNLISTED (rendered inside the marketing Layout) — home variants,
         unlinked offering pages, and pages parked for later. Same /unlisted
         prefix + noindex, but keep the header/footer chrome so they still
         look like real pages during review.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/unlisted" element={<Layout data-id="element-40" data-mp="38duzsxhwna60" />} data-id="element-39" data-mp="38emum63oh3v8">
          {/* Home variants / previews */}
          <Route path="architecture" element={<HomeArchitecture data-id="element-42" data-mp="2f6bxfjpjsnom" />} data-id="element-41" data-mp="2f7dk6lfxeym6" />
          <Route path="home-alt" element={<HomeAlternative data-id="element-44" data-mp="2k5ye634l4m26" />} data-id="element-43" data-mp="2f97vd81ebcd2" />
          <Route path="ecommerce" element={<EcommerceHome data-id="element-46" data-mp="2k8b0stlafklk" />} data-id="element-45" data-mp="2k4wyflcmjbe4" />
          <Route path="future" element={<FutureHome data-id="element-48" data-mp="2p6cm7l66rw07" />} data-id="element-47" data-mp="2p5kq898ofw9e" />
          <Route path="agents" element={<Agents data-id="element-50" data-mp="2u4e0ztwkx95v" />} data-id="element-49" data-mp="2p62r5j6aaynn" />
          <Route path="workspace" element={<WorkspaceHome data-id="element-52" data-mp="2u3vzoiv284r3" />} data-id="element-51" data-mp="2u4nv9tonq2hd" />
          <Route path="team-workspace" element={<HomeTeamWorkspace data-id="element-54" data-mp="1whf4bj5w017z" />} data-id="element-53" data-mp="2u21nbsxutkym" />
          {/* Offering pages, currently unlinked */}
          <Route path="enterprise" element={<Enterprise data-id="element-56" data-mp="21giaj6xlsdhj" />} data-id="element-55" data-mp="21f74t1klkvns" />
          <Route path="enterprise-platform" element={<EnterprisePlatform data-id="element-58" data-mp="26ea3m49zi5nb" />} data-id="element-57" data-mp="21dmddktkxsu5" />
          <Route path="bespoke" element={<Bespoke data-id="element-60" data-mp="26cyvjs9iabqe" />} data-id="element-59" data-mp="26d8qlu9er92y" />
          <Route path="consulting" element={<Consulting data-id="element-62" data-mp="2b6bk62e6qij5" />} data-id="element-61" data-mp="2b61teclbg7ce" />
          <Route path="security" element={<Security data-id="element-64" data-mp="1dqe9967w0ptp" />} data-id="element-63" data-mp="2b7moq4fgfubb" />
          {/* Content, parked until ready */}
          <Route path="blog" element={<Blog data-id="element-66" data-mp="1ioz4ssc72lbt" />} data-id="element-65" data-mp="1dobi2iv00qne" />
          <Route path="research" element={<Research data-id="element-68" data-mp="1inee5dt03mj8" />} data-id="element-67" data-mp="1io7bjo62kzop" />
          <Route path="case-studies" element={<CaseStudies data-id="element-70" data-mp="3vzsabtk92ew7" />} data-id="element-69" data-mp="3vzih7x3wrrma" />
          <Route path="changelog" element={<Changelog data-id="element-72" data-mp="3rxx9eq68e3iy" />} data-id="element-71" data-mp="3vyz0gjpzv99u" />
          {/* Use cases, parked until ready */}
          <Route path="use-cases/founders-execs" element={<FoundersExecs data-id="element-74" data-mp="3myaumcar8f81" />} data-id="element-73" data-mp="3ryorcy6wo2qr" />
          <Route path="use-cases/client-management" element={<ClientManagement data-id="element-76" data-mp="3hzpuecvi5vjl" />} data-id="element-75" data-mp="3mxix2vxlk7f4" />
          <Route path="use-cases/growth-marketing" element={<GrowthMarketing data-id="element-78" data-mp="3i23n4f2pmvnz" />} data-id="element-77" data-mp="3hyo79a17pili" />
          {/* Comparison pages, parked until ready */}
          <Route path="compare/chatgpt" element={<CompareChatGPT data-id="element-80" data-mp="389eedryjnvwc" />} data-id="element-79" data-mp="3d2r1tyrhlwni" />
        </Route>

        {/* ──────────────────────────────────────────────────────────────
         PUBLIC marketing site — indexable.
         ────────────────────────────────────────────────────────────── */}
        <Route path="/" element={<Layout data-id="element-82" data-mp="2z7585eab6oor" />} data-id="element-81" data-mp="2zat1acbud92y">
          {/* The four-pillar platform page is the home page. The previous
           Tasklet-style home now lives at /ai-coworker. */}
          <Route index element={<Alt data-id="element-84" data-mp="2pbu0th18cjr1" />} data-id="element-83" data-mp="2pc3j0ig19mm6" />
          <Route path="ai-coworker" element={<Home data-id="element-86" data-mp="2fmwtmabjbupd" />} data-id="element-85" data-mp="2pdek2ai3gg9l" />
          <Route path="terms" element={<Terms data-id="element-88" data-mp="2fm4zz51i071q" />} data-id="element-87" data-mp="2fmogchbi2ndn" />
          <Route path="affiliate-terms-reditus" element={<AffiliateTerms data-id="element-90" data-mp="2knc5exg64u58" />} data-id="element-89" data-mp="2kmj62hjjh6x4" />
          <Route path="privacy" element={<Privacy data-id="element-92" data-mp="26n1exvirthkl" />} data-id="element-91" data-mp="2kkg5x4oo9xeo" />
          <Route path="vulnerability-disclosure" element={<VulnerabilityDisclosure data-id="element-94" data-mp="26ouiv3201now" />} data-id="element-93" data-mp="26nta558ghdac" />
          <Route path="contact" element={<Contact data-id="element-96" data-mp="2aovbbrlxnzey" />} data-id="element-95" data-mp="2anl9qsfvg93k" />
          <Route path="support" element={<Support data-id="element-98" data-mp="2ye7f2byhrf9v" />} data-id="element-97" data-mp="2aqfw4pig43zm" />
          <Route path="developers" element={<Developers data-id="element-100" data-mp="2yepk9y34t3tx" />} data-id="element-99" data-mp="2yez413xl2er6" />
          <Route path="developers/api" element={<Developers data-id="element-102" data-mp="33bza3eufnhia" />} data-id="element-101" data-mp="33d0ui9xc9gco" />
          <Route path="developers/openapi" element={<Developers data-id="element-104" data-mp="2plvqkvmxu1wj" />} data-id="element-103" data-mp="33c8ywz3qrime" />
          <Route path="developers/authentication" element={<Developers data-id="element-106" data-mp="2pjt3okh30ow1" />} data-id="element-105" data-mp="2pkkrh94ftibr" />
          <Route path="developers/webhooks" element={<Developers data-id="element-108" data-mp="2tlo5rr6u76av" />} data-id="element-107" data-mp="2tmpfnsc49mr0" />
          <Route path="developers/mcp" element={<Developers data-id="element-110" data-mp="3hcjrc6kp959o" />} data-id="element-109" data-mp="2tkuwaighu2p1" />
          <Route path="new" element={<NewInfrastructure data-id="element-112" data-mp="3hap9j14q5t9t" />} data-id="element-111" data-mp="3hb8pwdeq89lq" />
          {/* /alt was this page's home before the swap. It renders the page
           directly rather than redirecting to "/": a Navigate drops the query
           string, so any preview or campaign link carrying params landed on a
           blank frame instead of the page. */}
          <Route path="alt" element={<Alt data-id="element-114" data-mp="1kem8okywg4qt" />} data-id="element-113" data-mp="1kecoje0jcrt1" />
          {/* Enterprise positioning variant. Lives at a top-level path so it is
           easy to open and preview directly; kept out of the index via
           NOINDEX_ROUTES. */}
          <Route path="enterprise-platform" element={<EnterprisePlatform data-id="element-116" data-mp="rrrq5tln7ffz" />} data-id="element-115" data-mp="rqqcdhd8seuk" />
          <Route path="careers" element={<Careers data-id="element-118" data-mp="mrvvikyu50d3" />} data-id="element-117" data-mp="mr3zxa58n2mt" />
        <Route path="pricing" element={<Pricing data-id="element-120" data-mp="11nv21r6aqsg4" />} data-id="element-119" data-mp="msf255j1c2bt" />
          <Route path="giga-for-chatgpt" element={<GigaForChatGPT data-id="element-122" data-mp="11p64nno09u5n" />} data-id="element-121" data-mp="11o4stgz613mv" />
          <Route path="chatgpt" element={<GigaForChatGPT2 data-id="element-124" data-mp="woid90vv7baw" />} data-id="element-123" data-mp="worvu3ekyg6k" />
          <Route path="company-brain" element={<CompanyBrain data-id="element-126" data-mp="2d30917dgnd5j" />} data-id="element-125" data-mp="wngra1db94ee" />
          <Route path="giga-for-chatgpt-2" element={<Navigate to="/chatgpt" replace data-id="element-128" data-mp="2d54jk85v7aes" />} data-id="element-127" data-mp="2d28iwc2mu7mn" />
          <Route path="creator-program" element={<CreatorProgram data-id="element-130" data-mp="283fa2wyaj4st" />} data-id="element-129" data-mp="284q9ylom7sen" />
          <Route path="viktor-alternative" element={<ViktorAlternative data-id="element-132" data-mp="hnv14eci9d8o" />} data-id="element-131" data-mp="hmtf5etyb6c6" />
          <Route path="integrations" element={<Integrations data-id="element-134" data-mp="mlmr32tlapa8" />} data-id="element-133" data-mp="howeipgzubtk" />
          {/* Data-driven per-integration pages, sourced from the Giga catalog
            (single source of truth). Replaces the old one-off pages. */}
          <Route path="integrations/:slug" element={<IntegrationDetail data-id="element-136" data-mp="vnnoq0sqjdcf" />} data-id="element-135" data-mp="vmkzryez3kz5" />
          <Route path="features/:slug" element={<FeatureDetail data-id="element-138" data-mp="10lxj496my9wz" />} data-id="element-137" data-mp="vnw01o97mali" />
          <Route path="features/connect-to-slack" element={<Navigate to="/features/giga-in-slack" replace data-id="element-140" data-mp="10juv1up1mquw" />} data-id="element-139" data-mp="10kmey89a305c" />
          <Route path="features/integrate-with-databases" element={<Navigate to="/features/connect-to-databases" replace data-id="element-142" data-mp="1abqtj6v1kizn" />} data-id="element-141" data-mp="15j7j2irni3z2" />
          <Route path="features/backed-by-a-full-computer" element={<Navigate to="/features/giga-cloud-computer" replace data-id="element-144" data-mp="1ec11uq6b3lho" />} data-id="element-143" data-mp="1adlgeqpvil6d" />
          <Route path="features/giga-cloud-computer" element={<Navigate to="/features/a-full-computer" replace data-id="element-146" data-mp="1jdqc43lpfv4p" />} data-id="element-145" data-mp="1jcfbudrgx5ic" />
          <Route path="features/browser-control" element={<Navigate to="/features/cloud-browser" replace data-id="element-148" data-mp="1o95qqxrjdv26" />} data-id="element-147" data-mp="1oagvp0wpx8uv" />
          <Route path="demo" element={<WatchGigaDemo data-id="element-150" data-mp="1ygi5bhoou4vh" />} data-id="element-149" data-mp="1yg893cd1v1hc" />
          <Route path="demo/book" element={<BookDemo data-id="element-152" data-mp="1tf2hjhv4lk9e" />} data-id="element-151" data-mp="1tg3ujrvpcgtr" />
        </Route>
      </Routes>
    </CopyModeProvider>;
}